import { yupResolver } from "@hookform/resolvers/yup";
import isEmpty from "lodash/isEmpty";
import { useFieldArray, useForm } from "react-hook-form";

import { Category } from "../../../../categories.types";
import { AttributeTypes } from "../../../../constants/enums";
import { SubChildCategorySchema } from "../../../../helpers";
import { SubChildCategoryFormData } from "../SubChildCategoryForm.types";

const DEFAULT_ATTRIBUTES = [
  {
    type: AttributeTypes.Required,
    name: "Color",
    isMain: true,
    values: []
  },
  {
    type: AttributeTypes.Required,
    name: "Size",
    isMain: true,
    values: []
  }
];

const useSubChildCategoryForm = (category: Category | null) => {
  const { name = "", image = null, attributes = [] } = category ?? {};

  const { control, watch, handleSubmit } = useForm<SubChildCategoryFormData>({
    defaultValues: {
      name,
      image,
      attributes: !isEmpty(attributes)
        ? attributes.map((attribute) => ({
            ...attribute,
            isMain: !!attribute.asMainInCategoryId
          }))
        : DEFAULT_ATTRIBUTES
    },
    resolver: yupResolver(SubChildCategorySchema)
  });

  const isMaxMainFilters =
    watch("attributes").filter(
      (attribute) => attribute.isMain && !attribute.delete
    ).length >= 2;

  const {
    fields: attributesFields,
    append: appendAttribute,
    remove: removeAttribute,
    update: updateAttribute
  } = useFieldArray<SubChildCategoryFormData, "attributes", "attributeId">({
    control,
    name: "attributes",
    keyName: "attributeId"
  });

  const onAppendAttribute = (type: AttributeTypes) => {
    return () =>
      appendAttribute({
        type,
        name: "",
        isMain: false,
        values: []
      });
  };

  const onRemoveAttribute = (attributeIndex: number) => {
    return () => {
      const attributeForRemove = attributesFields[attributeIndex];

      if (attributeForRemove.id) {
        return updateAttribute(attributeIndex, {
          ...attributeForRemove,
          delete: true
        });
      }

      removeAttribute(attributeIndex);
    };
  };

  return {
    control,
    attributes: attributesFields,
    isMaxMainFilters,
    onAppendAttribute,
    onRemoveAttribute,
    handleSubmit
  };
};

export default useSubChildCategoryForm;
